import { Box, LinearProgress } from '@mui/material';
import DisciplineStats from 'components/DisciplineStats';
import { useState, useEffect } from 'react';
import { useMember } from 'components/Providers';
import { useDispatch, useSelector } from 'react-redux';
import * as action from 'store/member/discipline-stats';
import { getMarkingPeriods } from 'store/lookup';
import PageHeader from 'components/AppBar/PageHeader';
import { useTranslation } from 'react-i18next';
// import NoRecords from 'components/NoRecords';

const StudentDisciplinePage = () => {
    const dispatch = useDispatch();
    const member = useMember();
    const { t, i18n } = useTranslation();
    const [disciplineItems, setDisciplineItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const currentMarkingPeriodId = useSelector(state => state.lookup.currentMarkingPeriodId);
    const session = useSelector(state => state.session)
    const { academicYear } = useSelector(state => state.session);
    const type = session?.person?.type
    const personMemberId = session.id
    const parentStudentId = useSelector(state => state.academic?.parentStudent?.selectedStudent?.personAccessViews[0]?.id);

    const [data, setData] = useState({
        stats: { totalPoints: 0, merits: 0, demerits: 0 },
        weekData: [],
        incidents: []
    });

    const fetchDisciplineData = async () => {
        const isParent = type === 'parent';
        const memberId = isParent ? parentStudentId : personMemberId;

        if (!memberId || !currentMarkingPeriodId) {
            setDisciplineItems([]);
            setData({
                stats: { totalPoints: 0, merits: 0, demerits: 0 },
                weekData: [],
                incidents: []
            });
            return;
        }

        try {
            setLoading(true);
            const actionResult = dispatch(action.retrieve({
                memberId,
                markingPeriodId: currentMarkingPeriodId
            }));
            
            actionResult.then(
                (result) => console.log('Promise resolved with:', result),
                (err) => console.log('Promise rejected with:', err)
            );
            
            const response = await actionResult.unwrap();
            setLoading(false);
            if (response) {
                setDisciplineItems(response);
            } else {
                setDisciplineItems([]);
            }
        } catch (error) {
            console.error('Discipline fetch error:', error);
            console.error('Error message:', error?.message);
            console.error('Error payload:', error?.payload);
            console.error('Full error object:', JSON.stringify(error, null, 2));
            setLoading(false);
            setDisciplineItems([]);
        }
    };

    useEffect(() => {
        if (currentMarkingPeriodId) {
            fetchDisciplineData();
        } else if (academicYear?.id) {
            dispatch(getMarkingPeriods({ id: academicYear.id }));
        }
    }, [dispatch, personMemberId, type, parentStudentId, currentMarkingPeriodId, academicYear?.id]);

    useEffect(() => {
        if (disciplineItems && disciplineItems.length > 0) {
            let merits = 0;
            let demerits = 0;

            disciplineItems.forEach(item => {
                const points = item.incidentItem?.point || 0;
                if (points > 0) {
                    merits += points;
                } else {
                    demerits += Math.abs(points);
                }
            });

            const totalPoints = merits - demerits;

            const now = new Date();
            const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
            const threeWeeksAgo = new Date(now.getTime() - 21 * 24 * 60 * 60 * 1000);
            const fourWeeksAgo = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);

            const weekScores = [0, 0, 0, 0];

            disciplineItems.forEach(item => {
                const incidentDate = new Date(item.incidentDate);
                const points = item.incidentItem?.point || 0;

                if (incidentDate >= oneWeekAgo) {
                    weekScores[0] += points;
                } else if (incidentDate >= twoWeeksAgo) {
                    weekScores[1] += points;
                } else if (incidentDate >= threeWeeksAgo) {
                    weekScores[2] += points;
                } else if (incidentDate >= fourWeeksAgo) {
                    weekScores[3] += points;
                }
            });

            const formatDate = (date) => date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' });

            const monthData = [
                { label: t('This Week'), score: weekScores[0] },
                { label: t('Last Week'), score: weekScores[1] },
                { label: formatDate(threeWeeksAgo), score: weekScores[2] },
                { label: formatDate(fourWeeksAgo), score: weekScores[3] },
            ];

            const incidents = disciplineItems
                .slice()
                .sort((a, b) => new Date(b.incidentDate) - new Date(a.incidentDate))
                .map(item => ({
                    date: new Date(item.incidentDate).toLocaleString(i18n.language, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    }),
                    title: item.incidentItem?.name || t('Unknown Incident'),
                    description: item.explanation || '',
                    staff: item.staff ? `${item.staff.lastName}, ${item.staff.firstName}` : t('Unknown'),
                    points: item.incidentItem?.point || 0,
                }));

            setData({
                stats: { totalPoints, merits, demerits },
                monthData,
                incidents
            });
        }
    }, [disciplineItems, member?.person?.id, i18n.language])

    console.log('Discipline Stats Data:', data);
    console.log('Discipline Items:', disciplineItems);

    return (
        <Box display={'flex'} flexDirection="column" gap={1}>
            <PageHeader title={'Discipline'} sx={{ mb: 2 }} />
            {loading && <LinearProgress />}
            <DisciplineStats
                stats={data.stats}
                monthData={data.monthData}
                incidents={data.incidents}
            />
        </Box>
    );
};

export default StudentDisciplinePage;